import React, { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import logo from "../assets/images/logo.svg";
import faqimage from "../assets/images/faq.svg";
import { Link } from "react-router-dom";
import { AddContact } from "../API/ContactAPI";
import { getAllFAQ } from "../API/FAQApi";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import { PuffLoader } from "react-spinners";

// Validation Schema
const ContactSchema = Yup.object().shape({
  Name: Yup.string().required("Name is required"),
  Email: Yup.string().email("Invalid email").required("Email is required"),
  Phone: Yup.string()
    .matches(/^[0-9]+$/, "Phone must be only digits")
    .required("Phone number is required"),
  Message: Yup.string().required("Message is required"),
});

// Initial Values
const initialValues = {
  Name: "",
  CCODE: "",
  Email: "",
  Phone: "",
  Message: "",
};

const Support = () => {
  const [activeIndex, setActiveIndex] = useState(null);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [faqData, setFaqData] = useState([]);
  const [faqLoading, setFaqLoading] = useState(true);

  const toggleFaq = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  // Fetch FAQs
  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        const res = await getAllFAQ();
        if (res) {
          setFaqData(res.responseData || []);
        }
      } catch (error) {
        console.error("Error fetching FAQs:", error);
      } finally {
        setFaqLoading(false);
      }
    };
    fetchFaqs();
  }, []);

  // Submit Handler
  const handleSubmit = async (values, { resetForm }) => {
    setIsFormLoading(true);
    try {
      const result = await AddContact(values);
      if (result.status === true) {
        resetForm();
      }
    } catch (err) {
      console.error("Submit error:", err);
    } finally {
      setIsFormLoading(false);
    }
  };

  return (
    <div className="bg-[#fff]">
      {isFormLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <PuffLoader color={"#83442B"} size={60} />
        </div>
      )}
      {/* Header */}
      <div className="bg-[#4B1E0C] text-white md:pb-20 pt-8 pb-12 text-center relative">
        {/* Logo */}

        <div className="container mx-auto mb-10">
          <Link to={'/'}>
            <img src={logo} alt="Rotiey Logo" className="h-8 md:h-10 mx-auto" />
          </Link>
        </div>

        <h1 className="text-2xl md:text-4xl font-semibold mb-4">
          👋 Hi there, How can we help you today?
        </h1>
        <p className="text-sm md:text-base">
          Need help with your Rotiey account, meals, or payments? Our support
          team has your back.
        </p>

        
      </div>

      {/* Contact Form */}
      <div className="container mx-auto px-4 py-10">

        <div className="loader-line"></div>
        <h2 className="text-xl md:text-3xl text-darktext font-semibold mb-1 mt-2">How can we help you?</h2>
        <p className="text-contentcolor mb-6">
          We're here to answer your questions and help you every step of the
          way.
        </p>
        <div className="bg-white border rounded-md shadow-sm p-4 md:p-6">
          <Formik
            initialValues={initialValues}
            validationSchema={ContactSchema}
            onSubmit={handleSubmit}
          >
            {({ values, setFieldValue, handleReset }) => (
              <Form className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                {/* Name */}
                <div>
                  <label htmlFor="Name" className="text-darktext block mb-1 text-sm md:text-base font-medium">Name</label>
                  <Field
                    type="text"
                    name="Name"
                    placeholder="Enter your name"
                    className="w-full border rounded-md px-3 py-2.5 md:py-3 focus:outline-none focus:ring-1 focus:ring-theme2 text-sm md:text-base"
                  />
                  <ErrorMessage
                    name="Name"
                    component="div"
                    className="text-red-500 text-xs md:text-sm mt-1"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="Email" className="text-darktext block mb-1 text-sm md:text-base font-medium">Email</label>
                  <Field
                    type="email"
                    name="Email"
                    placeholder="Enter your email"
                    className="w-full border rounded-md px-3 py-2.5 md:py-3 focus:outline-none focus:ring-1 focus:ring-theme2 text-sm md:text-base"
                  />
                  <ErrorMessage
                    name="Email"
                    component="div"
                    className="text-red-500 text-xs md:text-sm mt-1"
                  />
                </div>

                {/* Phone with Country Code */}
                <div className="md:col-span-2">
                  <label htmlFor="Phone" className="text-darktext block mb-1 text-sm md:text-base font-medium">Phone Number</label>
                  <PhoneInput
                    country={"in"}
                    value={values.CCODE + values.Phone}
                    onChange={(value, countryData) => {
                      setFieldValue("CCODE", "+" + countryData.dialCode);
                      setFieldValue(
                        "Phone",
                        value.replace(countryData.dialCode, "")
                      );
                    }}
                    inputClass="!w-full !py-2.5 md:!py-3 !border !rounded-md !focus:outline-none !focus:ring-1 !focus:ring-theme2 !text-sm md:!text-base"
                  />
                  <ErrorMessage
                    name="Phone"
                    component="div"
                    className="text-red-500 text-xs md:text-sm mt-1"
                  />
                </div>

                {/* Message */}
                <div className="md:col-span-2">
                  <label htmlFor="Message" className="text-darktext block mb-1 text-sm md:text-base font-medium">Message</label>
                  <Field
                    as="textarea"
                    rows="4"
                    name="Message"
                    placeholder="Hi there, I would like to..."
                    className="w-full border rounded-md px-3 py-2.5 md:py-3 focus:outline-none focus:ring-1 focus:ring-theme2 text-sm md:text-base resize-none"
                  />
                  <ErrorMessage
                    name="Message"
                    component="div"
                    className="text-red-500 text-xs md:text-sm mt-1"
                  />
                </div>

                {/* Buttons */}
                <div className="md:col-span-2 flex flex-col sm:flex-row justify-end gap-3 md:gap-4 mt-2">
                  <button
                    type="button"
                    onClick={handleReset}
                    disabled={isFormLoading}
                    className="px-4 py-2 md:px-6 md:py-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50 text-sm md:text-base font-medium"
                  >
                    CANCEL
                  </button>
                  <button
                    type="submit"
                    disabled={isFormLoading}
                    className="px-4 py-2 md:px-6 md:py-2 rounded-full bg-theme2 hover:bg-black transition text-white disabled:opacity-50 text-sm md:text-base font-medium"
                  >
                    {isFormLoading ? "Submitting..." : "SUBMIT"}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>

      {/* FAQs */}
      <div className="py-12">
        <h2 className="md:text-4xl text-2xl font-semibold mb-6 text-center md:px-5 px-3">
          Frequently Asked Questions
        </h2>
        <div className="container mx-auto px-4 grid md:grid-cols-3 md:gap-10 items-center">
          {/* Image */}
          <div className="flex my-3 md:my-0 justify-center md:col-span-1">
            <img
              src={faqimage}
              alt="FAQ illustration"
              className="w-full md:w-80"
            />
          </div>

          {/* FAQ List */}
          <div className="my-3 md:my-0 md:col-span-2">
            <div className="md:space-y-3 space-y-2">
              {faqLoading ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-theme2"></div>
                </div>
              ) : faqData.length > 0 ? (
                faqData.map((faq, index) => (
                  <div
                    key={faq.Id}
                    className="border rounded-md bg-white shadow-sm"
                  >
                    <button
                      onClick={() => toggleFaq(index)}
                      className="flex justify-between items-center w-full px-4 text-lg py-4 font-medium text-left"
                    >
                      {faq.Title}
                      {activeIndex === index ? (
                        <FaChevronUp className="text-contentcolor" />
                      ) : (
                        <FaChevronDown className="text-contentcolor" />
                      )}
                    </button>
                    {activeIndex === index && (
                      <div className="px-4 pb-4 text-gray-600 text-base">
                        {faq.Content}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-center text-gray-500">No FAQs available.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Support;